import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLocalStorage } from 'usehooks-ts';
import { queryClient } from '@/pages/_app';
export enum AppConfigKeys {
  blockAppNotifs = 'blockAppNotifs',
  blockAppVersion = 'blockAppVersion',
  paymentProvider = 'paymentProvider',
  appVersion = 'appVersion',
  alertMessage = 'alertMessage',
  onboardingCurrency = 'onboardingCurrency',
}
export type TAllowedCurrencies = 'SOL' | 'AVAX' | 'POL' | 'BNB' | 'APTOS' | 'SUI' | 'ETH_BASE' | 'ETH_OPTIMISM' | 'ETH_ARBITRUM' | 'ETH_ETHEREUM' | 'USDC_AVAX' | 'USDC_SOL' | 'USDC_BASE' | 'USDC_ARBITRUM' | 'USDC_POLYGON' | 'USDC_OPTIMISM' | 'USDC_ETHEREUM' | 'USDC_APTOS' | 'USDC_SUI' | 'USDT_BNB';
type AppConfig = {
  [AppConfigKeys.blockAppNotifs]: boolean;
  [AppConfigKeys.blockAppVersion]: boolean;
  [AppConfigKeys.paymentProvider]: 'moonpay' | 'coinbase';
  [AppConfigKeys.appVersion]?: string;
  [AppConfigKeys.alertMessage]?: string;
  [AppConfigKeys.onboardingCurrency]?: TAllowedCurrencies;
};
const AppConfigDefaults = {
  [AppConfigKeys.blockAppNotifs]: false,
  [AppConfigKeys.blockAppVersion]: false,
  [AppConfigKeys.paymentProvider]: 'moonpay',
  [AppConfigKeys.appVersion]: '',
  [AppConfigKeys.alertMessage]: '',
  [AppConfigKeys.onboardingCurrency]: 'SOL'
};
export const MoonpayMappings: { [key in TAllowedCurrencies]: string } = {
  ETH_BASE: 'eth_base',
  ETH_OPTIMISM: 'eth_optimism',
  ETH_ARBITRUM: 'eth_arbitrum',
  ETH_ETHEREUM: 'eth',
  SOL: 'SOL',
  AVAX: 'avax_cchain',
  BNB: 'bnb_bsc',
  POL: 'pol_polygon',
  APTOS: 'aptos',
  SUI: 'sui',
  USDC_AVAX: 'usdc_cchain',
  USDC_SOL: 'usdc_sol',
  USDC_BASE: 'usdc_base',
  USDC_ARBITRUM: 'usdc_arbitrum',
  USDC_POLYGON: 'usdc_polygon',
  USDC_OPTIMISM: 'usdc_optimism',
  USDC_ETHEREUM: 'usdc',
  USDT_BNB: 'usdt_bsc',
  //moonpay doesn't have these
  USDC_APTOS: 'aptos',
  USDC_SUI: 'sui'
};
export const OnramperMappings: { [key in TAllowedCurrencies]: string } = {
  ETH_BASE: 'eth_base',
  ETH_ARBITRUM: 'eth_arbitrum',
  ETH_ETHEREUM: 'eth',
  ETH_OPTIMISM: 'eth_optimism',
  SOL: 'sol',
  AVAX: 'avax_avaxc',
  BNB: 'bnb_bsc',
  POL: 'matic_polygon',
  APTOS: 'apt_aptos',
  SUI: 'sui_sui',
  USDC_AVAX: 'usdc_avaxc',
  USDC_SOL: 'usdc_sol',
  USDC_ARBITRUM: 'usdc_arbitrum',
  USDC_POLYGON: 'usdc_polygon',
  USDC_OPTIMISM: 'usdc_optimism',
  USDC_ETHEREUM: 'usdc_ethereum',
  USDC_BASE: 'usdc_base',
  USDT_BNB: 'usdt_bsc',
  USDC_APTOS: 'apt_aptos',
  USDC_SUI: 'sui_sui'
};
export const CoinbaseMappings: { [key in TAllowedCurrencies]: {
  name: string;
  chain: string;
} } = {
  ETH_BASE: {
    name: 'ETH',
    chain: 'base'
  },
  ETH_ARBITRUM: {
    name: 'ETH',
    chain: 'arbitrum'
  },
  ETH_OPTIMISM: {
    name: 'ETH',
    chain: 'optimism'
  },
  ETH_ETHEREUM: {
    name: 'ETH',
    chain: 'ethereum'
  },
  POL: {
    name: 'POL',
    chain: 'polygon'
  },
  SUI: {
    name: 'SUI',
    chain: 'sui'
  },
  APTOS: {
    name: 'APTOS',
    chain: 'aptos'
  },
  SOL: {
    name: 'SOL',
    chain: 'solana'
  },
  AVAX: {
    name: 'AVAX',
    chain: 'avacchain'
  },
  BNB: {
    name: 'SOL',
    chain: 'solana'
  },
  USDC_AVAX: {
    name: 'USDC',
    chain: 'avacchain'
  },
  USDC_SOL: {
    name: 'USDC',
    chain: 'solana'
  },
  USDC_BASE: {
    name: 'USDC',
    chain: 'base'
  },
  USDC_ARBITRUM: {
    name: 'USDC',
    chain: 'arbitrum'
  },
  USDC_OPTIMISM: {
    name: 'USDC',
    chain: 'optimism'
  },
  USDC_POLYGON: {
    name: 'USDC',
    chain: 'polygon'
  },
  USDC_ETHEREUM: {
    name: 'USDC',
    chain: 'ethereum'
  },
  USDT_BNB: {
    name: 'SOL',
    chain: 'solana'
  },
  USDC_APTOS: {
    name: 'USDC',
    chain: 'aptos'
  },
  USDC_SUI: {
    name: 'USDC',
    chain: 'sui'
  }
};
export default function useAppSettings() {
  const [appConfigLS, setAppConfigLS] = useLocalStorage<AppConfig | undefined>('appConfig', null);
  const {
    data: appConfig,
    isLoading: appConfigLoading
  } = useQuery({
    queryKey: ['appConfig'],
    queryFn: async (): Promise<AppConfig | undefined> => {
      let data = await fetch(`/api/admin?config_name=app-config`).then(res => res.json());
      const keys = data?.config?.data_json ? Object.keys(data?.config?.data_json) : [];
      const appConfigKeys = Object.values(AppConfigKeys);
      const missingKeys = appConfigKeys.filter(key => !keys.includes(key));
      if (missingKeys.length) {
        const currentConfig = data?.config?.data_json ? data?.config?.data_json : {};
        //add missing keys
        await fetch(`/api/admin?config_name=app-config`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: 'app-config',
            config: {
              data_json: {
                ...currentConfig,
                ...missingKeys.reduce((acc, key) => ({
                  ...acc,
                  [key]: AppConfigDefaults[key]
                }), {})
              }
            }
          })
        });
        // const updateResult = await updateResultReq.json();
        data = await fetch(`/api/admin?config_name=app-config`).then(res => res.json());
      }
      setAppConfigLS(data?.config?.data_json);
      return data?.config?.data_json;
    },
    refetchInterval: 1000 * 60 * 5
  });
  async function updateAppConfig(config: Partial<AppConfig>) {
    const newConfig = {
      ...appConfigLS,
      ...config
    };
    const result = await fetch(`/api/admin?config_name=app-config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: 'app-config',
        config: {
          data_json: newConfig
        }
      })
    });
    if (!result.ok) {
      console.error('Failed to update config');
    }
    queryClient.invalidateQueries({
      queryKey: ['appConfig']
    });
    toast.success('App Config Updated');
  }
  return {
    appConfig,
    appConfigLoading,
    appConfigLS,
    updateAppConfig
  };
}